/**
 * Rider Create Page
 * 
 * Page for creating a new rider with:
 * - RiderForm component
 * - Group data fetching
 * - Navigation after creation
 * 
 * Requirements: 4.5
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useRidersStore from '../store/ridersStore';
import { restClient, CreateRiderDto, UpdateRiderDto, Group } from '../api/rest-client';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { RiderForm } from '../components/features/riders/RiderForm';

/**
 * Rider Create Page Component
 */
export function RiderCreate() {
  const navigate = useNavigate();
  const { createRider } = useRidersStore();

  // Local state for groups
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch groups on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedGroups = await restClient.getGroups();
        setGroups(fetchedGroups);
        setLoading(false);
      } catch (err) {
        setError('Failed to load form data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (data: CreateRiderDto | UpdateRiderDto) => {
    try {
      // Capture rider_id from form data before submitting — the Lambda
      // returns only a message string, not the full rider object.
      const riderId = (data as any).rider_id;
      await createRider(data as CreateRiderDto);
      navigate(`/riders/${riderId}`, { replace: true });
    } catch (error) {
      // Error is handled by the form component
      throw error;
    }
  }, [createRider, navigate]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback(() => {
    window.history.length > 1 ? navigate(-1) : navigate('/riders');
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <Button variant="primary" onClick={handleCancel}>
                Back to Riders
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <Button variant="secondary" onClick={handleCancel}>
          ← Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create Rider</h1>
          <p className="text-gray-600 mt-1">Add a new rider or coach to the system</p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <RiderForm
          groups={groups}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Card>
    </div>
  );
}

export default RiderCreate;
